/**
 * The pager under a list, driven by the server's `page` block.
 *
 * ## Why it copes with not knowing the total
 *
 * Most endpoints report `page.total` and this renders a normal "1–25 of 340".
 * A folder listing cannot: children the caller may not read are dropped after
 * the query, so a `COUNT(*)` would name rows they can never reach. There
 * `total` is null, and MUI's own answer to that — `count={-1}` — is exactly
 * right: it renders "1–25 of more than 25" and hides the page count.
 *
 * The one thing `count={-1}` does not do is know when to stop, because MUI has
 * no way to tell. `page.hasMore` does know, so the next button is disabled from
 * it rather than from arithmetic on a total that may not exist.
 */
import { useTranslation } from 'react-i18next';
import TablePagination from '@mui/material/TablePagination';
import type { PageInfo } from '@forsys/shared';

export interface TablePagerProps {
  /** The `page` block from the response. Absent while the first request is in flight. */
  page: PageInfo | undefined;
  /** Zero-based, as MUI counts — `useListQuery` holds it in the same units. */
  current: number;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  /** Hidden when there is only one page. A pager over six rows is furniture. */
  hideWhenSingle?: boolean;
}

const ROWS_PER_PAGE = [10, 25, 50, 100];

export function TablePager(props: TablePagerProps) {
  const { page, current, onPageChange, onLimitChange, hideWhenSingle = true } = props;
  const { t } = useTranslation('common');

  if (!page) return null;
  if (hideWhenSingle && current === 0 && !page.hasMore) return null;

  return (
    <TablePagination
      component="div"
      // -1 is MUI's documented "server-side paging, total unknown". See above.
      count={page.total ?? -1}
      page={current}
      rowsPerPage={page.limit}
      rowsPerPageOptions={ROWS_PER_PAGE}
      onPageChange={(_event, next) => onPageChange(next)}
      onRowsPerPageChange={(event) => onLimitChange(Number(event.target.value))}
      labelRowsPerPage={t('pager.rowsPerPage')}
      labelDisplayedRows={({ from, to, count }) =>
        count === -1
          ? t('pager.rangeUnknown', { from, to })
          : t('pager.range', { from, to, count })
      }
      getItemAriaLabel={(type) =>
        type === 'next' ? t('pager.nextPage') : t('pager.previousPage')
      }
      slotProps={{
        // Without this the next button stays enabled forever on an unknown
        // total, and pressing it lands the reader on an empty page.
        actions: { nextButton: { disabled: !page.hasMore } },
      }}
    />
  );
}
